import { getControlPlaneRuntimeConfig } from "@/app/runtime-config";

export class ControlApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly body: unknown,
  ) {
    super(`Control API error ${status}: ${code}`);
    this.name = "ControlApiError";
  }
}

export interface ControlFetchOptions {
  method?: "GET" | "POST" | "DELETE";
  body?: unknown;
}

function notifyUnauthorized() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("auth:unauthorized"));
  }
}

function readControlApiErrorCode(status: number, body: unknown) {
  if (body && typeof body === "object") {
    const record = body as Record<string, unknown>;
    const directCode = record.code;
    if (typeof directCode === "string" && directCode.trim()) {
      return directCode;
    }
    const error = record.error;
    if (error && typeof error === "object") {
      const nestedCode = (error as Record<string, unknown>).code;
      if (typeof nestedCode === "string" && nestedCode.trim()) {
        return nestedCode;
      }
    }
  }
  return `HTTP_${status}`;
}

export function applyControlAuthHeaders(headers: Headers | Record<string, string>) {
  const runtimeConfig = getControlPlaneRuntimeConfig();
  const serviceId = runtimeConfig.serviceId?.trim() || "";
  const staticToken = runtimeConfig.staticAuth?.token?.trim() || "";
  if (headers instanceof Headers) {
    if (serviceId && !headers.has("x-openclaw-service-id")) {
      headers.set("x-openclaw-service-id", serviceId);
    }
    if (staticToken && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${staticToken}`);
    }
    return;
  }
  if (serviceId) {
    headers["x-openclaw-service-id"] = serviceId;
  }
  if (staticToken) {
    headers.Authorization = `Bearer ${staticToken}`;
  }
}

async function fetchWithTimeout(input: RequestInfo | URL, init?: RequestInit) {
  const runtimeConfig = getControlPlaneRuntimeConfig();
  const controlRequestTimeoutMs = Math.max(
    1_000,
    Number(runtimeConfig.requestTimeoutMs ?? 10_000) || 10_000,
  );
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), controlRequestTimeoutMs);
  try {
    return await fetch(input, {
      ...init,
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error(
        `Control Plane request timed out after ${Math.floor(controlRequestTimeoutMs / 1000)} seconds.`, { cause: error },
      );
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

export async function controlFetch<T>(
  path: string,
  options?: ControlFetchOptions | unknown,
): Promise<T> {
  const normalizedOptions =
    options && typeof options === "object" && ("method" in options || "body" in options)
      ? (options as ControlFetchOptions)
      : ({ body: options } satisfies ControlFetchOptions);
  const method = normalizedOptions.method ?? "POST";
  const body = normalizedOptions.body;
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  const runtimeConfig = getControlPlaneRuntimeConfig();
  const baseUrl = runtimeConfig.apiBaseUrl?.trim() || "";
  if (method !== "GET") {
    headers["Content-Type"] = "application/json";
  }
  applyControlAuthHeaders(headers);

  const res = await fetchWithTimeout(`${baseUrl}${path}`, {
    method,
    headers,
    credentials: "include",
    body: method === "GET" || body === undefined ? undefined : JSON.stringify(body),
  });

  if (!res.ok) {
    let errorBody: unknown;
    try {
      errorBody = await res.json();
    } catch {
      errorBody = await res.text().catch(() => null);
    }
    if (res.status === 401) {
      notifyUnauthorized();
    }
    throw new ControlApiError(res.status, readControlApiErrorCode(res.status, errorBody), errorBody);
  }

  return res.json() as Promise<T>;
}

export async function controlAuthorityFetch<T>(path: string): Promise<T> {
  const issuer = getControlPlaneRuntimeConfig().oidc?.issuer?.trim();
  if (!issuer) {
    throw new Error("OIDC issuer is not configured.");
  }

  const response = await fetchWithTimeout(`${issuer.replace(/\/$/, "")}${path}`, {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    let errorBody: unknown;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = await response.text().catch(() => null);
    }
    // Authority account endpoints are an optional side-channel for tenant discovery.
    // A 401 here must not clear the Control Plane session tokens.
    throw new ControlApiError(response.status, readControlApiErrorCode(response.status, errorBody), errorBody);
  }

  return response.json() as Promise<T>;
}
